import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import {useEffect, useRef, useState} from "react";
import {subWeeks} from "date-fns";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Filters from "./components/filters.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import DispatchArrivalService from "../../../services/reports/dispatch-arrival-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import Content from "./components/content.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";

const initialFilter = () => ({
    dtStart: subWeeks(new Date(), 1),
    dtEnd: new Date(),
    numberCheckbox: "",
    garrison: "",
    nature: [],
    nature_groups: [],
    streetNames: [],
    placeNames: [],
    neighborhood: [],
    allSectors: [],
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    numberCheckbox: "Talão Nº",
    garrison: "Guarnição",
    nature: "Naturezas",
    nature_groups: "Grupo de naturezas",
    streetNames: "Logradouro",
    placeNames: "Local pré-cadastrado",
    neighborhood: "Bairro",
    allSectors: "Setor",
};

export default function DispatchArrival() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [reportData, setReportData] = useState(null);

    const [tableColumns, setTableColumns] = useState(() => ({
        attendance: {label: "Atendimento", visible: true},
        dispatch: {label: "Despacho", visible: true},
        nature: {label: "Natureza", visible: true},
        address: {label: "Endereço", visible: true},
        garrison: {label: "Guarnição", visible: true},
        attendanceTime: {label: "Tempo até início do despacho", visible: true},
        arrivalTime: {label: "Tempo entre despacho e chegada", visible: true},
        mileage: {label: "KM percorrido", visible: true},
        duration: {label: "Duração do despacho", visible: true}
    }));

    const [headerSelected, setHeaderSelected] = useState(-1);
    const title = "Tempo de chegada";
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, "year");

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            };

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                numberCheckbook: filters.numberCheckbox ?? "",
                garrison: filters.garrison ?? "",
                reason: filters.nature?.map(i => i.value) ?? [],
                reason_groups: filters.nature_groups?.map(i => i.value) ?? [],
                street: filters.streetNames?.map(i => i.label) ?? [],
                place: filters.placeNames?.map(i => i.value) ?? [],
                neighborhood: filters.neighborhood?.map(i => i.value) ?? [],
                sector: filters.allSectors?.map(i => i.value) ?? [],
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels, true);

            const response = await DispatchArrivalService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório.", error);
        } finally {
            setIsLoading(false);
        };
    };

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        };
        setCollapsed(prev => !prev);
    };

    useGlobalKey("Enter", apply);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            tableColumns={tableColumns}
                            reportData={reportData}
                            data={reportData?.result?.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-0" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            apply={apply}
                            clearSearch={clearSearch}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.result?.length === 0 ? "mt-[59px]" : "mt-[61px]"} print:!mt-[-15px] transform ${!reportData || reportData?.result?.length === 0 ? "!bg-[#f3f3f4] !border-none !-translate-y-[5px]" : ""} ${collapsed ? "ml-2" : ""}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.result?.length === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                            <NotAppliedContent reportData={reportData} />
                            <NoDataContent reportData={reportData} data={reportData?.result?.length} />
                            <Content
                                reportData={reportData}
                                data={reportData?.result?.length}
                                entity={headerSelected}
                                title={title}
                                collapsed={collapsed}
                                setIsLoading={setIsLoading}
                                printComponentRef={printComponentRef}
                                tableColumns={tableColumns}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}